import * as _ from 'lodash';

import * as Urls from '../_external/util/urls';

import * as OpKinds from '../opKinds';
import * as HL from '../hl';
import * as Graph from '../graph';
import {constNumber} from '../consts';
import {replaceInputVariables} from '../refineHelpers';
import * as Types from '../model/types';
import * as String from '../utils/string';
import * as JSONNan from '../utils/jsonnan';
import * as Obj from '../utils/obj';

const makeRunOp = OpKinds.makeTaggingStandardOp;

const runArgTypes = {
  run: 'run' as const,
};

const runArgDescriptions = `A ${Graph.docType('run')}`;

const isTableTypeHistoryKeyType = (id: string) => {
  return ['table-file', 'partitioned-table', 'joined-table'].includes(id);
};

export const opGetRunTag = OpKinds.makeTagGetterOp({
  name: 'tag-run',
  tagName: 'run',
  tagType: 'run',
});

export const opRunInternalId = makeRunOp({
  hidden: true,
  name: 'run-internalId',
  argTypes: runArgTypes,
  description: `Returns the internal id of the ${Graph.docType('run')}`,
  argDescriptions: {run: runArgDescriptions},
  returnValueDescription: `The internal id of the ${Graph.docType('run')}`,
  returnType: inputTypes => 'string',
  resolver: ({run}) => {
    return run.id;
  },
});

// Keep this hidden until we're sure users need it.
export const opRunId = makeRunOp({
  hidden: true,
  name: 'run-id',
  argTypes: runArgTypes,
  description: `Returns the id of the ${Graph.docType('run')}`,
  argDescriptions: {run: runArgDescriptions},
  returnValueDescription: `The id of the ${Graph.docType('run')}`,
  returnType: inputTypes => 'string',
  resolver: ({run}) => run.name,
});

export const opRunName = makeRunOp({
  name: 'run-name',
  argTypes: runArgTypes,
  description: `Returns the name of the ${Graph.docType('run')}`,
  argDescriptions: {run: runArgDescriptions},
  returnValueDescription: `The name of the ${Graph.docType('run')}`,
  returnType: inputTypes => 'string',
  resolver: ({run}) => run.displayName,
});

export const opRunJobType = makeRunOp({
  name: 'run-jobType',
  argTypes: runArgTypes,
  description: `Returns the job type of the ${Graph.docType('run')}`,
  argDescriptions: {run: runArgDescriptions},
  returnValueDescription: `The job type of the ${Graph.docType('run')}`,
  returnType: inputTypes => 'string',
  resolver: ({run}) => run.jobType,
});

export const opRunLink = makeRunOp({
  hidden: true,
  name: 'run-link',
  argTypes: runArgTypes,
  description: `Returns the link to the ${Graph.docType('run')}`,
  argDescriptions: {run: runArgDescriptions},
  returnValueDescription: `The link to the ${Graph.docType('run')}`,
  returnType: inputTypes => 'link',
  resolver: ({run}) => ({
    name: run.displayName,
    url: Urls.run({
      entityName: run.project.entityName,
      projectName: run.project.name,
      name: run.name,
    }),
  }),
});

export const opRunProject = makeRunOp({
  hidden: true,
  name: 'run-project',
  argTypes: runArgTypes,
  description: `Returns the ${Graph.docType('project')} of the ${Graph.docType(
    'run'
  )}`,
  argDescriptions: {run: runArgDescriptions},
  returnValueDescription: `The ${Graph.docType(
    'project'
  )} of the ${Graph.docType('run')}`,
  returnType: inputTypes => 'project',
  resolver: ({run}) => run.project,
});

export const opRunUser = makeRunOp({
  name: 'run-user',
  argTypes: runArgTypes,
  description: `Returns the ${Graph.docType('user')} of the ${Graph.docType(
    'run'
  )}`,
  argDescriptions: {run: runArgDescriptions},
  returnValueDescription: `The ${Graph.docType('user')} of the ${Graph.docType(
    'run'
  )}`,
  returnType: inputTypes => 'user',
  resolver: ({run}) => run.user,
});

export const opRunCreatedAt = makeRunOp({
  name: 'run-createdAt',
  argTypes: runArgTypes,
  description: `Returns the created at datetime of the ${Graph.docType('run')}`,
  argDescriptions: {run: runArgDescriptions},
  returnValueDescription: `The created at datetime of the ${Graph.docType(
    'run'
  )}`,
  returnType: inputTypes => 'date',
  resolver: ({run}) => new Date(run.createdAt + 'Z'),
});

export const opRunHeartbeatAt = makeRunOp({
  name: 'run-heartbeatAt',
  argTypes: runArgTypes,
  description: `Returns the last heartbeat datetime of the ${Graph.docType(
    'run'
  )}`,
  argDescriptions: {run: runArgDescriptions},
  returnValueDescription: `The last heartbeat datetime of the ${Graph.docType(
    'run'
  )}`,
  returnType: inputTypes => 'date',
  resolver: ({run}) => new Date(run.heartbeatAt + 'Z'),
});

// Hiding... does job type want to be something more than a string?
export const opRunJobtype = makeRunOp({
  hidden: true,
  name: 'run-jobtype',
  argTypes: runArgTypes,
  description: `Returns the job type of the ${Graph.docType('run')}`,
  argDescriptions: {run: runArgDescriptions},
  returnValueDescription: `The job type of the ${Graph.docType('run')}`,
  returnType: inputTypes => 'string',
  resolver: ({run}) => run.jobType,
});

// Create a Type from config or summary json.
// Note: this is not complete! It doesn't handle nested keys
// at all.
export const wandbJsonType = (json: any): Types.Type => {
  const propertyTypes: {[key: string]: Types.Type} = {};
  for (const key of Object.keys(json ?? {})) {
    const val = json[key];
    if (val == null) {
      propertyTypes[key] = 'none';
    } else if (typeof val === 'string') {
      propertyTypes[key] = 'string';
    } else if (typeof val === 'number') {
      propertyTypes[key] = 'number';
    } else {
      // Handle the case of artifact files in summary.
      // TODO: handle more than table-file
      if (isTableTypeHistoryKeyType(val._type)) {
        if (val.artifact_path != null) {
          propertyTypes[key] = Types.filePathToType(
            Types.parseArtifactRef(val.artifact_path).assetPath
          );
        } else {
          propertyTypes[key] = Types.filePathToType(val.path);
        }
      }
    }
  }
  return {type: 'typedDict', propertyTypes};
};

export const wandbJsonWithArtifacts = (json: {[key: string]: any}) => {
  return _.mapValues(json, val => {
    if (
      val != null &&
      isTableTypeHistoryKeyType(val._type) &&
      val.artifact_path != null
    ) {
      const parsedRef = Types.parseArtifactRef(val.artifact_path);
      return {
        ...val,
        artifact: {id: parsedRef.artifactId},
        path: parsedRef.assetPath,
      };
    }
    return val;
  });
};

export const opRunConfig = makeRunOp({
  name: 'run-config',
  argTypes: runArgTypes,
  description: `Returns the config ${Graph.docType(
    'typedDict'
  )} of the ${Graph.docType('run')}`,
  argDescriptions: {run: runArgDescriptions},
  returnValueDescription: `The config ${Graph.docType(
    'typedDict'
  )} of the ${Graph.docType('run')}`,
  returnType: inputTypes => Types.typedDict({}),
  resolver: ({run}) => {
    const parsed = JSONNan.JSONparseNaN(run.config) ?? {};
    const fixed = _.mapValues(parsed, (child: any) => child.value);
    return wandbJsonWithArtifacts(fixed);
  },
  // TODO: resolveOutputType does not perform all the correct
  // unwrapping!
  resolveOutputType: async (inputTypes, node, executableNode, client) => {
    const runConfigNode = replaceInputVariables(executableNode);
    const config: any = await client.query(runConfigNode);
    if (_.isArray(config)) {
      return Types.union(config.map(wandbJsonType));
    } else {
      return wandbJsonType(config ?? {});
    }
  },
});

export const opRunSummary = makeRunOp({
  name: 'run-summary',
  argTypes: runArgTypes,
  description: `Returns the summary ${Graph.docType(
    'typedDict'
  )} of the ${Graph.docType('run')}`,
  argDescriptions: {run: runArgDescriptions},
  returnValueDescription: `The summary ${Graph.docType(
    'typedDict'
  )} of the ${Graph.docType('run')}`,
  returnType: inputTypes => Types.typedDict({}),
  resolver: ({run}) => {
    const res = JSONNan.JSONparseNaN(run.summaryMetrics) ?? {};
    return wandbJsonWithArtifacts(res);
  },
  // This TODO is important! Need to use the same OpTypes behaviors
  // we do in returnType in all resolveOutputType calls
  // TODO: resolveOutputType does not perform all the correct
  // unwrapping!
  resolveOutputType: async (inputTypes, node, executableNode, client) => {
    const runSummaryNode = replaceInputVariables(executableNode);
    const summary: any = await client.query(runSummaryNode);
    if (_.isArray(summary)) {
      return Types.union(summary.map(wandbJsonType));
    } else {
      return wandbJsonType(summary ?? {});
    }
  },
});

// TODO: missing test
export const opRunHistoryKeyInfo = makeRunOp({
  hidden: true,
  name: '_run-historykeyinfo',
  argTypes: runArgTypes,
  description: `Returns the history key info for each key of the ${Graph.docType(
    'run'
  )}`,
  argDescriptions: {run: runArgDescriptions},
  returnValueDescription: `The history key info for each key of the ${Graph.docType(
    'run'
  )}`,
  returnType: inputTypes => Types.typedDict({}),
  resolver: ({run}) => run.historyKeys ?? {keys: {}, sets: []},
});

// TODO: missing test
export const opRunHistory = makeRunOp({
  name: 'run-history',
  argTypes: runArgTypes,
  description: `Returns the log history of the ${Graph.docType('run')}`,
  argDescriptions: {run: runArgDescriptions},
  returnValueDescription: `The log history of the ${Graph.docType('run')}`,
  returnType: inputTypes => Types.list(Types.typedDict({})),
  resolver: ({run}) =>
    run?.history?.map((row: string) => {
      const res = JSONNan.JSONparseNaN(row);
      return wandbJsonWithArtifacts(res);
    }) ?? [],
  // TODO: resolveOutputType does not perform all the correct
  // unwrapping!

  resolveOutputType: async (inputTypes, node, executableNode, client) => {
    // TODO: We don't need this firstRunNode thing here... This function is
    // just all wrong now. See more correct implementation in opRunSummary/
    // opRunConfig

    // See opRunSummary for comment
    const firstRunNode = HL.mapNodes(executableNode, mapped => {
      if (
        mapped.nodeType === 'output' &&
        mapped.fromOp.name === 'index' &&
        mapped.fromOp.inputs.index.nodeType === 'var'
      ) {
        return {
          nodeType: 'output',
          // wrong type, but we don't need type to execute
          // the summary call... TODO: there must be a
          // better way
          type: 'invalid',
          fromOp: {
            ...mapped.fromOp,
            inputs: {
              ...mapped.fromOp.inputs,
              index: constNumber(0),
            },
          },
        };
      }
      return mapped;
    }) as Types.OutputNode;
    const run = firstRunNode.fromOp.inputs.run;

    // Execute ourself!
    const runHistoryNode = opRunHistory({run});
    // TODO: This is very expensive right now! It reads the entire history
    // and all keys, for the table logic below. Totally unnecessary.
    let history = await client.query(runHistoryNode);

    const runHistoryTypeNode = opRunHistoryKeyInfo({run});
    let historyKeyInfo = await client.query(runHistoryTypeNode);
    if (_.isArray(historyKeyInfo)) {
      history = history[0];
      historyKeyInfo = historyKeyInfo[0];
    }

    const propertyTypes: {[key: string]: Types.Type} = {};
    for (const key of Object.keys(historyKeyInfo.keys)) {
      const val = historyKeyInfo.keys[key];
      const valType = val.typeCounts[0].type;
      if (valType === 'string') {
        propertyTypes[key] = 'string';
      } else if (valType === 'number') {
        propertyTypes[key] = 'number';
      } else if (isTableTypeHistoryKeyType(valType)) {
        const vals = history.map((hRow: any) => hRow[key]).filter(Obj.notEmpty);
        if (vals.length !== 0) {
          const val0 = vals[0];
          if (val0.artifact_path == null) {
            // TODO: This will throw an error in the non-artifact case.
            // Shouldn't be an error. Ideally we'd load from the run.
            throw new Error('invalid');
          }
          if (isTableTypeHistoryKeyType(val0._type)) {
            if (val0.artifact_path != null) {
              propertyTypes[key] = Types.filePathToType(
                Types.parseArtifactRef(val0.artifact_path).assetPath
              );
            } else {
              propertyTypes[key] = Types.filePathToType(val0.path);
            }
          }
        }
      }
    }

    const outputType: Types.Type = {
      type: 'list',
      objectType: {
        type: 'typedDict',
        propertyTypes,
      },
    };
    return outputType;
  },
});

export const opRunLoggedArtifactVersion = makeRunOp({
  name: 'run-loggedArtifactVersion',
  argTypes: {...runArgTypes, artifactVersionName: 'string'},
  description: `Returns the ${Graph.docType(
    'artifactVersion'
  )} logged by the ${Graph.docType('run')} for a given name and alias`,
  argDescriptions: {
    run: runArgDescriptions,
    artifactVersionName: `The name:alias of the ${Graph.docType(
      'artifactVersion'
    )}`,
  },
  returnValueDescription: `The ${Graph.docType(
    'artifactVersion'
  )} logged by the ${Graph.docType('run')} for a given name and alias`,
  returnType: inputTypes => 'artifactVersion',
  resolver: ({run, artifactVersionName}) => {
    // tslint:disable-next-line: prefer-const
    let [artifactName, version] = String.splitOnce(artifactVersionName, ':');
    if (version == null) {
      version = 'latest';
    }
    const foundEdge = run.outputArtifacts.edges.find(
      (edge: any) =>
        (edge.node.versionIndex.toString() === version?.slice(1) ||
          edge.node.aliases.some((a: any) => a.alias === version)) &&
        edge.node.artifactSequence.name === artifactName
    );
    if (foundEdge == null) {
      // We don't want to error here. in the case that you have
      // runs.loggedArtifactVersion("a:v1"), not all runs will
      // have the version.
      return null;
    }
    return foundEdge.node;
  },
});

export const opRunLoggedArtifactVersions = makeRunOp({
  name: 'run-loggedArtifactVersions',
  argTypes: runArgTypes,
  description: `Returns all of the ${Graph.docType('artifactVersion', {
    plural: true,
  })} logged by the ${Graph.docType('run')}`,
  argDescriptions: {run: runArgDescriptions},
  returnValueDescription: `The ${Graph.docType('artifactVersion', {
    plural: true,
  })} logged by the ${Graph.docType('run')}`,
  returnType: inputTypes => Types.list('artifactVersion'),
  resolver: ({run}) => run.outputArtifacts.edges.map((edge: any) => edge.node),
});

export const opRunUsedArtifactVersions = makeRunOp({
  name: 'run-usedArtifactVersions',
  argTypes: runArgTypes,
  description: `Returns all of the ${Graph.docType('artifactVersion', {
    plural: true,
  })} used by the ${Graph.docType('run')}`,
  argDescriptions: {run: runArgDescriptions},
  returnValueDescription: `The ${Graph.docType('artifactVersion', {
    plural: true,
  })} used by the ${Graph.docType('run')}`,
  returnType: inputTypes => Types.list('artifactVersion'),
  resolver: ({run}) => run.inputArtifacts.edges.map((edge: any) => edge.node),
});

export const opRunRuntime = makeRunOp({
  name: 'run-runtime',
  argTypes: runArgTypes,
  description: `Returns the runtime in seconds of the ${Graph.docType('run')}`,
  argDescriptions: {run: runArgDescriptions},
  returnValueDescription: `The runtime in seconds of the ${Graph.docType(
    'run'
  )}`,
  returnType: inputTypes => 'number',
  resolver: ({run}) => {
    return run.computeSeconds;
  },
});
